/**
 * Refine
 * 絞り込み機能
 *
 * @example
 * HTML:
 * <div class="js-refine">
 *   <input type="checkbox" class="js-refine-trigger" name="category" value="art" data-filter="category">
 *   <select class="js-refine-trigger" name="year" data-filter="year">
 *     <option value="">全て</option>
 *     <option value="2025">2025年</option>
 *   </select>
 * </div>
 * <div class="js-refine-target">
 *   <div data-category="art" data-year="2025">項目1</div>
 *   <div data-category="music" data-year="2024">項目2</div>
 * </div>
 *
 * JS: new Refine();
 */
export default class Refine {
  constructor(options = {}) {
    this.options = {
      containerSelector: '.js-refine',
      triggerSelector: '.js-refine-trigger',
      targetSelector: '.js-refine-target',
      itemSelector: '> *', // ターゲット内の直接の子要素
      hiddenClass: 'is-hidden',
      noResultsClass: 'is-no-results',
      animationDuration: 300,
      // もっと見る機能
      itemsPerPage: 6,
      moreButtonSelector: '.js-refine-more-button',
      moreContainerSelector: '.js-refine-more',
      ...options
    };

    this.containers = [];
    this.init();
  }

  /**
   * 初期化
   */
  init() {
    const containers = document.querySelectorAll(this.options.containerSelector);
    console.log('[Refine] Found containers:', containers.length);
    if (containers.length === 0) return;

    containers.forEach(container => {
      console.log('[Refine] Setting up container:', container);
      this.setupContainer(container);
    });

    console.log('[Refine] Initialized with', containers.length, 'containers');
  }

  /**
   * コンテナのセットアップ
   */
  setupContainer(container) {
    const triggerElements = container.querySelectorAll(this.options.triggerSelector);
    const targets = document.querySelectorAll(this.options.targetSelector);

    console.log('[Refine] Triggers found:', triggerElements.length);
    console.log('[Refine] Targets found:', targets);
    
    if (triggerElements.length === 0 && targets.length === 0) {
      console.log('[Refine] No triggers and targets found, skipping setup');
      return;
    }

    const triggers = [];

    // トリガー要素を収集（直接のトリガーまたは内部のinput要素）
    triggerElements.forEach(element => {
      if (element.tagName.toLowerCase() === 'input' || 
          element.tagName.toLowerCase() === 'select' || 
          element.tagName.toLowerCase() === 'textarea') {
        // 直接トリガー要素の場合
        triggers.push(element);
      } else {
        // コンテナ要素の場合、内部のinput要素を探す
        const inputs = element.querySelectorAll('input, select, textarea');
        inputs.forEach(input => triggers.push(input));
      }
    });

    // data-items-per-page属性を読み取る（レスポンシブ対応）
    const itemsPerPage = container.dataset.itemsPerPage ? 
                         parseInt(container.dataset.itemsPerPage, 10) : null;
    const itemsPerPageSP = container.dataset.itemsPerPageSp ? 
                           parseInt(container.dataset.itemsPerPageSp, 10) : null;
    
    console.log('[Refine] Items per page:', itemsPerPage, 'SP:', itemsPerPageSP);

    const containerData = {
      container,
      triggers: triggers,
      targets: Array.from(targets),
      filters: {},
      currentPage: 0,
      itemsPerPage: itemsPerPage,
      itemsPerPageSP: itemsPerPageSP,
      useLoadMore: itemsPerPage !== null || itemsPerPageSP !== null,
      moreButton: null,
      moreContainer: null,
      filteredItems: []
    };
    
    // デバッグ: ターゲット内のアイテム数を確認
    let totalItems = 0;
    targets.forEach(target => {
      const items = target.querySelectorAll(':scope > *');
      totalItems += items.length;
      console.log('[Refine] Target items:', items.length, 'in', target);
    });
    console.log('[Refine] Total items across all targets:', totalItems);

    // イベントリスナーの設定
    triggers.forEach(trigger => {
      const eventType = this.getEventType(trigger);
      trigger.addEventListener(eventType, (e) => {
        this.handleFilterChange(e, containerData);
      });
    });

    // もっと見るボタンの設定（useLoadMoreがtrueの場合のみ）
    if (containerData.useLoadMore) {
      this.setupMoreButton(containerData);
      
      // 初期表示時にアイテム数をチェックしてボタンを制御
      const currentItemsPerPage = this.getCurrentItemsPerPage(containerData);
      console.log('[Refine] Initial check - total items:', totalItems, 'items per page:', currentItemsPerPage);
      
      if (containerData.moreButton && totalItems <= currentItemsPerPage) {
        containerData.moreButton.style.display = 'none';
        console.log('[Refine] Hiding button on init - not enough items');
      }
    }

    // リサイズイベントのハンドリング（レスポンシブ対応）
    if (containerData.itemsPerPage !== null && containerData.itemsPerPageSP !== null) {
      const resizeHandler = () => {
        // 画面サイズが変わったらフィルターを再適用
        this.applyFilters(containerData);
      };
      window.addEventListener('resize', resizeHandler);
      containerData.resizeHandler = resizeHandler;
    }

    this.containers.push(containerData);
    
    // 初期表示の調整
    this.applyFilters(containerData);
  }

  /**
   * 要素のタイプに応じたイベントタイプを取得
   */
  getEventType(element) {
    const tagName = element.tagName.toLowerCase();
    const type = element.type;

    if (tagName === 'select') return 'change';
    if (type === 'checkbox' || type === 'radio') return 'change';
    if (type === 'text' || type === 'search') return 'input';

    return 'change';
  }

  /**
   * 現在の画面サイズに応じたページあたりのアイテム数を取得
   */
  getCurrentItemsPerPage(containerData) {
    if (containerData.itemsPerPage !== null && containerData.itemsPerPageSP !== null) {
      // レスポンシブ対応の場合（PC用はdata-items-per-page、SP用はdata-items-per-page-sp）
      // 768px以上をPC表示とする（CSSの ovsp: min-width:768px と一致）
      return window.innerWidth >= 768 ? containerData.itemsPerPage : containerData.itemsPerPageSP;
    } else if (containerData.itemsPerPage !== null) {
      // 固定値の場合
      return containerData.itemsPerPage;
    }
    return this.options.itemsPerPage; // デフォルト値
  }

  /**
   * フィルター変更のハンドリング
   */
  handleFilterChange(event, containerData) {
    const trigger = event.target;
    const filterKey = trigger.dataset.filter || trigger.name;

    // console.log('🔍 [Refine Debug] Filter change detected:', {
    //   trigger: trigger,
    //   filterKey: filterKey,
    //   triggerValue: trigger.value,
    //   checked: trigger.checked,
    //   dataset: trigger.dataset
    // });

    if (!filterKey) {
      console.warn('Refine: Filter key not found for trigger:', trigger);
      return;
    }

    // フィルター値の更新
    this.updateFilterValue(trigger, filterKey, containerData);

    // console.log('🔍 [Refine Debug] Current filters after update:', containerData.filters);

    // 絞り込み実行
    this.applyFilters(containerData);

    // カスタムイベントの発火
    this.dispatchFilterEvent(containerData);
  }

  /**
   * フィルター値の更新
   */
  updateFilterValue(trigger, filterKey, containerData) {
    const tagName = trigger.tagName.toLowerCase();
    const type = trigger.type;

    if (type === 'checkbox') {
      // チェックボックスの場合、配列で管理
      if (!containerData.filters[filterKey]) {
        containerData.filters[filterKey] = [];
      }

      if (trigger.checked) {
        if (!containerData.filters[filterKey].includes(trigger.value)) {
          containerData.filters[filterKey].push(trigger.value);
        }
      } else {
        containerData.filters[filterKey] = containerData.filters[filterKey].filter(
          value => value !== trigger.value
        );
      }

      // 空配列の場合は削除
      if (containerData.filters[filterKey].length === 0) {
        delete containerData.filters[filterKey];
      }
    } else if (tagName === 'select' || type === 'radio' || type === 'text') {
      // セレクト、ラジオ、テキストの場合
      if (trigger.value && trigger.value.trim() !== '') {
        containerData.filters[filterKey] = trigger.value;
      } else {
        delete containerData.filters[filterKey];
      }
    }
  }

  /**
   * フィルターの適用
   */
  applyFilters(containerData) {
    // ページ数をリセット
    containerData.currentPage = 0;
    
    let totalVisibleCount = 0;
    let filteredItems = [];
    
    containerData.targets.forEach(target => {
      // 直接の子要素を取得
      const items = Array.from(target.children);

      items.forEach(item => {
        const isVisible = this.checkItemVisibility(item, containerData.filters);
        
        if (isVisible) {
          filteredItems.push(item);
          totalVisibleCount++;
        }
        
        // 一旦全て非表示
        this.hideItem(item);
      });
    });

    // もっと見る機能を使用する場合は最初のページ分だけ表示
    if (containerData.useLoadMore) {
      const currentItemsPerPage = this.getCurrentItemsPerPage(containerData);
      const itemsToShow = filteredItems.slice(0, currentItemsPerPage);
      itemsToShow.forEach(item => this.showItem(item));
      
      // もっと見るボタンの表示/非表示
      this.updateMoreButton(containerData, filteredItems.length);
      
      // 初期表示時にアイテム数がページ設定以下の場合はボタンを非表示
      if (filteredItems.length <= currentItemsPerPage && containerData.moreButton) {
        containerData.moreButton.style.display = 'none';
        console.log('[Refine] Hiding more button - items:', filteredItems.length, 'per page:', currentItemsPerPage);
      }
    } else {
      // もっと見る機能を使わない場合は全て表示
      filteredItems.forEach(item => this.showItem(item));
    }

    // 結果がない場合の処理（コンテナに付与）
    this.handleNoResults(containerData.container, totalVisibleCount === 0);
    
    // フィルタリングされたアイテムを保存
    containerData.filteredItems = filteredItems;
  }

  /**
   * アイテムの表示可否をチェック
   */
  checkItemVisibility(item, filters) {
    // フィルターが空の場合は全て表示
    if (Object.keys(filters).length === 0) return true;

    // console.log('🔍 [Refine Debug] Checking item:', {
    //   item: item,
    //   itemDataset: item.dataset,
    //   filters: filters
    // });

    // 全てのフィルター条件をチェック
    for (const [filterKey, filterValue] of Object.entries(filters)) {
      const itemValue = item.dataset[filterKey];
      
      // console.log('🔍 [Refine Debug] Filter check:', {
      //   filterKey: filterKey,
      //   filterValue: filterValue,
      //   itemValue: itemValue,
      //   isArray: Array.isArray(filterValue)
      // });
      
      if (!itemValue) {
        // console.log('🔍 [Refine Debug] Item rejected: no data value for', filterKey);
        return false;
      }

      if (Array.isArray(filterValue)) {
        // 配列の場合（チェックボックス）
        const matches = filterValue.some(value => itemValue.includes(value));
        // console.log('🔍 [Refine Debug] Array check result:', matches);
        if (!matches) {
          return false;
        }
      } else {
        // 単一値の場合
        const matches = itemValue.includes(filterValue);
        // console.log('🔍 [Refine Debug] Single value check result:', matches);
        if (!matches) {
          return false;
        }
      }
    }

    // console.log('🔍 [Refine Debug] Item passed all filters');
    return true;
  }

  /**
   * アイテムを表示
   */
  showItem(item) {
    item.classList.remove(this.options.hiddenClass);
    item.style.display = '';
  }

  /**
   * アイテムを非表示
   */
  hideItem(item) {
    item.classList.add(this.options.hiddenClass);
    // CSSでdisplay: noneが定義されていない場合の保険
    if (!item.classList.contains(this.options.hiddenClass)) {
      item.style.display = 'none';
    }
  }

  /**
   * 結果なしの処理
   */
  handleNoResults(target, isEmpty) {
    if (isEmpty) {
      target.classList.add(this.options.noResultsClass);
    } else {
      target.classList.remove(this.options.noResultsClass);
    }
  }

  /**
   * カスタムイベントの発火
   */
  dispatchFilterEvent(containerData) {
    const event = new CustomEvent('refine:filter', {
      detail: {
        filters: containerData.filters,
        container: containerData.container
      }
    });
    
    containerData.container.dispatchEvent(event);
  }

  /**
   * フィルターのクリア
   */
  clearFilters(container = null) {
    const targets = container ? [this.containers.find(c => c.container === container)] : this.containers;
    
    targets.forEach(containerData => {
      if (!containerData) return;

      // フィルター値をクリア
      containerData.filters = {};

      // UI要素をリセット
      containerData.triggers.forEach(trigger => {
        if (trigger.type === 'checkbox' || trigger.type === 'radio') {
          trigger.checked = false;
        } else if (trigger.tagName.toLowerCase() === 'select') {
          trigger.selectedIndex = 0;
        } else {
          trigger.value = '';
        }
      });

      // 全アイテムを表示
      this.applyFilters(containerData);
    });
  }

  /**
   * 現在のフィルター状態を取得
   */
  getFilters(container = null) {
    if (container) {
      const containerData = this.containers.find(c => c.container === container);
      return containerData ? containerData.filters : {};
    }
    
    return this.containers.map(c => ({
      container: c.container,
      filters: c.filters
    }));
  }

  /**
   * もっと見るボタンの設定
   */
  setupMoreButton(containerData) {
    const button = document.querySelector(this.options.moreButtonSelector);
    console.log('[Refine] More button found:', button);
    if (!button) {
      console.log('[Refine] No more button found');
      return;
    }
    
    button.addEventListener('click', () => {
      console.log('[Refine] More button clicked');
      this.loadMore(containerData);
    });
    
    containerData.moreButton = button;
    containerData.moreContainer = document.querySelector(this.options.moreContainerSelector);
    console.log('[Refine] More button setup complete');
  }

  /**
   * もっと見るボタンの更新
   */
  updateMoreButton(containerData, totalItems) {
    if (!containerData.useLoadMore) return;
    
    const currentItemsPerPage = this.getCurrentItemsPerPage(containerData);
    const shownItems = (containerData.currentPage + 1) * currentItemsPerPage;
    
    // moreContainerの制御
    if (containerData.moreContainer) {
      if (totalItems > shownItems) {
        containerData.moreContainer.style.display = '';
      } else {
        containerData.moreContainer.style.display = 'none';
      }
    }
    
    // moreButton単体の制御
    if (containerData.moreButton) {
      if (totalItems <= currentItemsPerPage || shownItems >= totalItems) {
        containerData.moreButton.style.display = 'none';
        console.log('[Refine] Hiding button - total:', totalItems, 'shown:', shownItems, 'per page:', currentItemsPerPage);
      } else {
        containerData.moreButton.style.display = '';
      }
    }
  }

  /**
   * もっと見る処理
   */
  loadMore(containerData) {
    if (!containerData.filteredItems || !containerData.useLoadMore) return;
    
    containerData.currentPage++;
    const currentItemsPerPage = this.getCurrentItemsPerPage(containerData);
    const startIndex = containerData.currentPage * currentItemsPerPage;
    const endIndex = startIndex + currentItemsPerPage;
    
    const itemsToShow = containerData.filteredItems.slice(startIndex, endIndex);
    itemsToShow.forEach(item => this.showItem(item));
    
    // ボタンの表示更新
    this.updateMoreButton(containerData, containerData.filteredItems.length);
  }

  /**
   * インスタンスの破棄
   */
  destroy() {
    this.containers.forEach(containerData => {
      containerData.triggers.forEach(trigger => {
        const eventType = this.getEventType(trigger);
        trigger.removeEventListener(eventType, this.handleFilterChange);
      });
      
      // リサイズイベントの削除
      if (containerData.resizeHandler) {
        window.removeEventListener('resize', containerData.resizeHandler);
      }
    });

    this.containers = [];
  }
}